import { useMemo } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { strCode } from "../../../consts";
import { t } from "../../../services";
import { useTableData } from "../../../hooks";

import {
  CircleIconButton,
  FlexContainers,
  Paragraph2,
  SVG,
  Table,
  WrapperContainer
} from "../../../components";
import { ConsentsTableColumns } from "./ConsentsTableColumns";
import { DraftConsentsTableColumns } from "./DraftConsentsTableColumns";
import { ConsentsTableAdditionalElement, ConsentsTableWrapper } from "./styled";

const ConsentsTable = ({
  consentsData,
  isDraft,
  showConsentElements,
  callbackFn,
  tooltipLabel,
  onCreateConsent
}) => {
  const loading = useSelector((store) => store.consents?.loading);
  const data = useTableData({ loading, data: consentsData, loadingPlaceholdersRows: 5 });

  const columns = useMemo(() => (isDraft ? DraftConsentsTableColumns : ConsentsTableColumns), []);
  const sortDataBy = isDraft
    ? { id: "updatedAtTs", desc: true }
    : {
        id: "version",
        desc: true
      };

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [sortDataBy]
      }
    },
    useSortBy,
    usePagination
  );

  return (
    <WrapperContainer mt={24} width="100%">
      {consentsData && (
        <ConsentsTableWrapper withAdditionalElement={consentsData?.length > 0}>
          <Table
            tableInstance={tableInstance}
            maxTableHeight={"200px"}
            onRowClick={showConsentElements}
            callbackFn1={callbackFn}
            tooltipLabel={tooltipLabel}
            isLoading={loading}
            componentHeight={112}
            maxVisibleRows={1}
            component={
              !consentsData?.length ? (
                <ConsentsTableAdditionalElement>
                  <WrapperContainer
                    Container={FlexContainers.FlexColumnCenter}
                    gap={16}
                    height={consentsData?.length || loading ? 60 : 82}
                    mt={consentsData?.length || loading ? 0 : 24}>
                    {!consentsData?.length && !loading && (
                      <Paragraph2>{t(strCode.NO_CONSENTS_DATA)}</Paragraph2>
                    )}

                    <CircleIconButton
                      onClick={onCreateConsent}
                      tooltipLabel={t(strCode.ADD_NEW_ELEMENT_LABEL)}>
                      <SVG.PlusIcon />
                    </CircleIconButton>
                  </WrapperContainer>
                </ConsentsTableAdditionalElement>
              ) : (
                <ConsentsTableAdditionalElement>
                  <WrapperContainer
                    Container={FlexContainers.FlexColumnCenter}
                    gap={16}
                    height={consentsData?.length ? 60 : 82}
                    mt={consentsData?.length ? 0 : 24}>
                    {!consentsData?.length && (
                      <Paragraph2>{t(strCode.NO_MARKETS_ELEMENTS_DATA)}</Paragraph2>
                    )}

                    <CircleIconButton
                      onClick={onCreateConsent}
                      tooltipLabel={t(strCode.ADD_NEW_ELEMENT_LABEL)}>
                      <SVG.PlusIcon />
                    </CircleIconButton>
                  </WrapperContainer>
                </ConsentsTableAdditionalElement>
              )
            }
            withAdditionalElement={consentsData?.length > 0}
            modalTable
          />
        </ConsentsTableWrapper>
      )}
    </WrapperContainer>
  );
};

ConsentsTable.propTypes = {
  consentsData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      version: PropTypes.number,
      market: PropTypes.string.isRequired,
      appType: PropTypes.string.isRequired,
      activatedAtTs: PropTypes.number,
      deactivatedAtTs: PropTypes.number,
      status: PropTypes.string.isRequired,
      modifiedBy: PropTypes.string.isRequired
    })
  ).isRequired,
  isDraft: PropTypes.bool,
  showConsentElements: PropTypes.func.isRequired,
  callbackFn: PropTypes.func.isRequired,
  tooltipLabel: PropTypes.string,
  onCreateConsent: PropTypes.func.isRequired
};

export default ConsentsTable;
